/**
 * DP268-E — /agreement/:token
 *
 * The page a contractor or supplier opens from the emailed link (DP268-D).
 * No login. Everything is keyed by the token; the page never sends a
 * contractor_id. Two faces, decided by the server's status:
 *
 *   sent / in_progress  →  the agreement: read each card, tick it, sign.
 *   signed              →  what is owed now (insurance, W-9 with due dates)
 *                          and where the Site Office link will arrive.
 *   410 / 404           →  a closed sheet: the link has expired or been replaced.
 *
 * Endpoints (DP268-D, public router, all unauthenticated):
 *   GET  /api/agreement-links/{token}
 *   POST /api/agreement-links/{token}/accept-card   { card_id, accepted }
 *   POST /api/agreement-links/{token}/sign          { signer_name, signer_title }
 *
 * Branding: the GET carries `branding` (company_name, logo_url, firm_skin,
 * surface_overrides, type_*). It is handed to utils/skin.cacheBranding so the
 * page paints in the tenant's own skin, surfaces and faces. No colour, size
 * or family is written in this file or its stylesheet — tokens only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import '@/styles/agreement-page.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A bare client: the app's shared axios attaches a staff JWT when one is
// cached in this browser, and a 401 there bounces to /login. This page must
// behave identically for a contractor on a phone and for Chad previewing it.
const bare = axios.create();

// The card text names the firm as {{designer}} (DP64); it resolves to the
// tenant's own name here exactly as ContractorAgreement.renderBullet does.
const fill = (text, firm) => String(text || '').replace(/\{\{designer\}\}/g, firm || '');

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function AgreementPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [closed, setClosed] = useState(null);   // { title, text } when 404/410
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await bare.get(`${API}/agreement-links/${token}`);
      setData(r.data);
      if (r.data?.branding) cacheBranding(r.data.branding);
      setClosed(null);
    } catch (e) {
      const s = e?.response?.status;
      setClosed(s === 410
        ? { title: 'This link has been replaced', text: 'A newer link was sent, or this one has expired. Use the most recent email, or ask for a new one.' }
        : { title: 'This link is not valid', text: 'Check the address in the email, or ask for a new link.' });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || [];
  const accepted = useMemo(() => new Set(data?.agreement?.accepted_cards || []), [data]);
  const allTicked = cards.length > 0 && cards.every((c) => accepted.has(c.id));
  const isTerms = data?.kind === 'terms';
  const verb = isTerms ? 'acknowledge' : 'sign';
  const docName = isTerms ? 'purchasing terms' : (data?.agreement_name || 'agreement');

  const toggle = async (card) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const r = await bare.post(`${API}/agreement-links/${token}/accept-card`, { card_id: card.id, accepted: !accepted.has(card.id) });
      setData((d) => ({ ...d, agreement: r.data.agreement }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not save. Try again.');
    } finally { setBusy(false); }
  };

  const sign = async () => {
    if (busy || !allTicked || !name.trim() || !title.trim()) return;
    setBusy(true); setError('');
    try {
      await bare.post(`${API}/agreement-links/${token}/sign`, { signer_name: name.trim(), signer_title: title.trim() });
      await load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'The signature did not save. Try again.');
    } finally { setBusy(false); }
  };

  const head = (
    <header className="kma-head">
      {data?.branding?.logo_url
        ? <img className="kma-head__logo" src={data.branding.logo_url} alt="" />
        : null}
      <div className="kma-head__k">
        <b>{data?.firm_name || ''}</b>{data?.project_name ? <> · {data.project_name}</> : null}
      </div>
    </header>
  );

  const foot = (
    <footer className="kma-foot">
      {data?.firm_name || ''} · this link is personal to {data?.party_name || 'you'}
    </footer>
  );

  if (closed) {
    return (
      <main className="kma-page" data-testid="agreement-page">
        <div className="kma-sheet">
          <div className="kma-closed">
            <h1 className="kma-title">{closed.title}</h1>
            <p className="kma-lead">{closed.text}</p>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="kma-page" data-testid="agreement-page">
        <div className="kma-sheet"><div className="kma-closed"><p className="kma-lead">Opening…</p></div></div>
      </main>
    );
  }

  if (data.status === 'signed') {
    const owed = data.owed || [];
    return (
      <main className="kma-page" data-testid="agreement-page">
        <div className="kma-sheet">
          {head}
          <div className="kma-body">
            <div className="kma-done" data-testid="agreement-signed">
              <p className="kma-done__t">
                {isTerms ? 'Terms acknowledged' : 'Signed'}{data.agreement?.signed_at ? ` · ${fmtDate(data.agreement.signed_at)}` : ''}
                {data.agreement?.signer_name ? ` by ${data.agreement.signer_name}` : ''}
              </p>
              <p className="kma-lead" style={{ margin: 0 }}>
                {owed.length
                  ? `Thank you. ${data.firm_name} now needs the documents below. Each has a date; past it, work on the project pauses until it arrives.`
                  : `Thank you. Nothing else is needed from you right now.`}
              </p>
              {owed.length > 0 && (
                <ul className="kma-owed" data-testid="agreement-owed">
                  {owed.map((o) => (
                    <li key={o.id}>
                      <span>{o.label}</span>
                      <span>{o.state === 'ok' ? 'on file' : o.due_at ? `due ${fmtDate(o.due_at)}` : 'needed'}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="kma-hint">
                Send them by reply to the email this link came in, or upload them in your Site Office.
              </p>
              {data.office_url ? (
                <a className="kma-btn" href={data.office_url} data-testid="agreement-office-link">Open your Site Office</a>
              ) : null}
            </div>
          </div>
          {foot}
        </div>
      </main>
    );
  }

  return (
    <main className="kma-page" data-testid="agreement-page">
      <div className="kma-sheet">
        {head}
        <div className="kma-body">
          <h1 className="kma-title">Before you can see the project, one thing</h1>
          <p className="kma-lead">
            {data.firm_name} has added <b>{data.party_name}</b> to <b>{data.project_name}</b>
            {data.party_kind ? <> as <b>{data.party_kind}</b></> : null}.
            {' '}Read the {docName} below, tick each part, and {verb} it. Nothing else on the project shows until this is done.
          </p>
          <div className="kma-cards" data-testid="agreement-cards">
            {cards.map((c) => {
              const on = accepted.has(c.id);
              return (
                <div
                  key={c.id}
                  className="kma-card"
                  data-on={on ? 'true' : 'false'}
                  role="checkbox"
                  aria-checked={on}
                  tabIndex={0}
                  onClick={() => toggle(c)}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(c); } }}
                  data-testid={`agreement-card-${c.id}`}
                >
                  <div className="kma-card__box">{on ? '✓' : ''}</div>
                  <div>
                    <p className="kma-card__t">{c.title}</p>
                    <ul className="kma-card__b">
                      {(c.bullets || []).map((b, i) => <li key={i}>{fill(b, data.firm_name)}</li>)}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="kma-count">{accepted.size} of {cards.length} read</p>
          <div className="kma-sig" data-testid="agreement-sign">
            <div className="kma-sig__lbl">{isTerms ? 'Acknowledged by' : 'Signed by'}</div>
            <input
              className="kma-in kma-in--name"
              placeholder="Type your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              data-testid="agreement-signer-name"
            />
            <input
              className="kma-in"
              placeholder="Your title (Owner, Estimator…)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoComplete="organization-title"
              data-testid="agreement-signer-title"
            />
            <button
              type="button"
              className="kma-btn"
              disabled={busy || !allTicked || !name.trim() || !title.trim()}
              onClick={sign}
              data-testid="agreement-sign-btn"
            >
              {isTerms ? 'I have read it — acknowledge' : 'I have read it — sign'}
            </button>
          </div>
          {error ? <p className="kma-hint kma-hint--late" role="alert">{error}</p> : (
            <p className="kma-hint">
              {allTicked ? 'Typing your name and title here is your signature.' : 'Tick every part to enable the signature.'}
            </p>
          )}
        </div>
        {foot}
      </div>
    </main>
  );
}
